export type LogMatchLine = {
  idx: number;
  text: string;
  isMatch: boolean;
};

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

/**
 * Filters logs to only the lines matching query plus N lines of context before/after.
 * - Case-insensitive substring match.
 * - De-duplicates overlapping context windows.
 * - Returns in original order.
 */
export function filterLogLinesWithContext(
  lines: string[],
  queryRaw: string,
  beforeRaw: number,
  afterRaw: number,
): { lines: LogMatchLine[]; matchCount: number } {
  const query = String(queryRaw ?? '').trim();
  const before = clampInt(beforeRaw, 0, 200);
  const after = clampInt(afterRaw, 0, 200);

  if (!query) {
    return {
      lines: lines.map((text, idx) => ({ idx, text, isMatch: false })),
      matchCount: 0,
    };
  }

  const q = query.toLowerCase();
  const include = new Map<number, LogMatchLine>();
  let matchCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const text = lines[i] ?? '';
    const isMatch = text.toLowerCase().includes(q);
    if (!isMatch) continue;
    matchCount++;

    const start = Math.max(0, i - before);
    const end = Math.min(lines.length - 1, i + after);
    for (let j = start; j <= end; j++) {
      const t = lines[j] ?? '';
      const jm = j === i;
      const existing = include.get(j);
      if (existing) {
        // preserve match flag if any window marks it as match
        include.set(j, { ...existing, isMatch: existing.isMatch || jm });
      } else {
        include.set(j, { idx: j, text: t, isMatch: jm });
      }
    }
  }

  const out = Array.from(include.values()).sort((a, b) => a.idx - b.idx);
  return { lines: out, matchCount };
}

export function highlightMatches(text: string, queryRaw: string): Array<{ text: string; isHit: boolean }> {
  const query = String(queryRaw ?? '').trim();
  if (!query) return [{ text, isHit: false }];

  // Case-insensitive splitting without regex escaping overhead.
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts: Array<{ text: string; isHit: boolean }> = [];

  let i = 0;
  while (i < text.length) {
    const hit = lower.indexOf(q, i);
    if (hit === -1) {
      parts.push({ text: text.slice(i), isHit: false });
      break;
    }
    if (hit > i) parts.push({ text: text.slice(i, hit), isHit: false });
    parts.push({ text: text.slice(hit, hit + query.length), isHit: true });
    i = hit + query.length;
  }

  return parts.length ? parts : [{ text, isHit: false }];
}

